import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Plus } from 'lucide-react';
import { getImageUrl } from '@/lib/supabase';
import type { FloorplanVersion, FloorplanMessage } from '@/types';

interface FloorplanVersionDropdownProps {
  versions: FloorplanVersion[];
  messagesByVersion: Record<string, FloorplanMessage[]>;
  activeVersionId: string | null;
  activeMessageNumber: number | null;
  onSelectVersion: (versionId: string, messageNumber: number | null) => void;
  onNewVersion: () => void;
  newVersionPrice?: number;
}

export const FloorplanVersionDropdown: React.FC<FloorplanVersionDropdownProps> = ({
  versions,
  messagesByVersion,
  activeVersionId,
  activeMessageNumber,
  onSelectVersion,
  onNewVersion,
  newVersionPrice = 5,
}) => {
  const [open, setOpen] = useState(false);
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set());

  const activeVersion = versions.find((v) => v.id === activeVersionId) ?? null;
  const activeMessages = activeVersion ? messagesByVersion[activeVersion.id] ?? [] : [];
  const activeMessage =
    activeMessages.find(
      (m) => m.messageNumber === activeMessageNumber && m.status === 'success'
    ) ?? null;

  const activeThumbPath =
    activeMessage?.resultingStoragePath ?? activeVersion?.initialStoragePath ?? null;
  // Cache-buster with fresh UUID per row so overwritten same-path files show current content.
  const activeThumbCacheKey = activeMessage?.id ?? activeVersion?.id ?? '';
  const activeThumbUrl = activeThumbPath
    ? `${getImageUrl(activeThumbPath)}?v=${activeThumbCacheKey}`
    : null;

  const activeLabel = activeVersion
    ? `Version ${activeVersion.versionNumber}${
        activeMessage ? ` · Nachricht ${activeMessage.messageNumber}` : ' · Ausgangs-Stand'
      }`
    : 'Keine Version';

  const toggleExpand = (versionId: string) => {
    setExpandedVersions((prev) => {
      const next = new Set(prev);
      if (next.has(versionId)) {
        next.delete(versionId);
      } else {
        next.add(versionId);
      }
      return next;
    });
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-2 px-3 py-1.5 bg-card rounded-lg border transition-all ${
          open
            ? 'border-primary shadow-[0_0_0_3px_rgba(254,127,46,0.12)]'
            : 'border-border hover:border-primary/50'
        }`}
      >
        {activeThumbUrl ? (
          <div className="relative w-10 h-8 bg-neutral-100 rounded overflow-hidden shrink-0">
            <img src={activeThumbUrl} alt="" className="w-full h-full object-cover" />
            {activeVersion && (
              <span className="absolute top-0.5 right-0.5 bg-primary text-white text-[7px] font-bold px-1 rounded-sm leading-tight">
                V{activeVersion.versionNumber}
              </span>
            )}
          </div>
        ) : (
          <div className="w-10 h-8 bg-neutral-100 rounded shrink-0 border border-border" />
        )}
        <div className="flex flex-col items-start text-left">
          <span className="text-xs font-semibold text-foreground">{activeLabel}</span>
          <span className="text-[9px] text-primary">Klick zum Wechseln</span>
        </div>
        <ChevronDown
          className={`size-3.5 text-primary transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              className="absolute top-full mt-2 right-0 w-80 bg-white border border-border rounded-xl shadow-2xl p-2 z-50 max-h-[70vh] overflow-y-auto"
            >
              <p className="px-2 py-1 text-[10px] font-bold text-muted-foreground uppercase tracking-wide">
                Ausgangs-Versionen
              </p>

              {versions.length === 0 ? (
                <p className="px-2 py-4 text-xs text-muted-foreground text-center">
                  Noch keine Versionen.
                </p>
              ) : (
                versions.map((v) => {
                  const vMessages = (messagesByVersion[v.id] ?? []).filter((m) => m.status === 'success');
                  const isActiveVersion = v.id === activeVersionId;
                  const isExpanded = expandedVersions.has(v.id);
                  // ?v=<versionId> as cache-buster — initial_storage_path is
                  // set once per version so versionId is a stable key.
                  const thumbUrl = `${getImageUrl(v.initialStoragePath)}?v=${v.id}`;

                  return (
                    <div
                      key={v.id}
                      className={`rounded-md p-1 mb-1 ${
                        isActiveVersion ? 'bg-neutral-50 border border-border' : ''
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          if (vMessages.length > 0) {
                            toggleExpand(v.id);
                          } else {
                            onSelectVersion(v.id, null);
                            setOpen(false);
                          }
                        }}
                        className="flex items-center gap-2 p-1.5 w-full rounded hover:bg-neutral-100 text-left"
                      >
                        <img
                          src={thumbUrl}
                          alt=""
                          className="w-9 h-7 object-cover rounded border border-border shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold">
                            Version {v.versionNumber}
                            {isActiveVersion && (
                              <span className="text-primary font-normal"> · aktiv</span>
                            )}
                          </div>
                          <div className="text-[9px] text-muted-foreground">
                            {vMessages.length > 0
                              ? `${vMessages.length} Nachrichten`
                              : '0 Nachrichten · nur Ausgangs-Stand'}
                          </div>
                        </div>
                        <ChevronDown
                          className={`size-3 text-muted-foreground transition-transform ${
                            isExpanded ? 'rotate-180' : '-rotate-90'
                          }`}
                        />
                      </button>

                      <AnimatePresence initial={false}>
                        {isExpanded && vMessages.length > 0 && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.14 }}
                            className="pl-3 flex flex-col gap-0.5 mt-0.5 overflow-hidden"
                          >
                            {/* Ausgangs-Stand entry */}
                            <button
                              onClick={() => {
                                onSelectVersion(v.id, null);
                                setOpen(false);
                              }}
                              className={`flex items-center gap-1.5 p-1 rounded text-left transition-colors ${
                                isActiveVersion && activeMessageNumber === null
                                  ? 'bg-primary/10 border border-primary'
                                  : 'bg-white border border-border hover:bg-neutral-50'
                              }`}
                            >
                              <img
                                src={thumbUrl}
                                alt=""
                                className="w-7 h-5 object-cover rounded-sm shrink-0"
                              />
                              <span className="text-[10px] flex-1">Ausgangs-Stand</span>
                            </button>

                            {vMessages.map((m) => {
                              const isActiveMsg =
                                isActiveVersion && activeMessageNumber === m.messageNumber;
                              // ?v=<msg.id> — message UUID is fresh after each
                              // hard-reset + resend, so it cache-busts same-path
                              // overwrites (v{N}/msg{M}.png on upsert: true).
                              const msgThumb = m.resultingStoragePath
                                ? `${getImageUrl(m.resultingStoragePath)}?v=${m.id}`
                                : thumbUrl;
                              return (
                                <button
                                  key={m.id}
                                  onClick={() => {
                                    onSelectVersion(v.id, m.messageNumber);
                                    setOpen(false);
                                  }}
                                  className={`flex items-center gap-1.5 p-1 rounded text-left transition-colors ${
                                    isActiveMsg
                                      ? 'bg-primary/10 border border-primary'
                                      : 'bg-white border border-border hover:bg-neutral-50'
                                  }`}
                                >
                                  <img
                                    src={msgThumb}
                                    alt=""
                                    className="w-7 h-5 object-cover rounded-sm shrink-0"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-[10px]">Nach Nachricht {m.messageNumber}</div>
                                    <div className="text-[8px] text-muted-foreground line-clamp-1 italic">
                                      „{m.userPrompt}"
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })
              )}

              <div className="h-px bg-border my-1.5" />

              <button
                type="button"
                onClick={() => {
                  onNewVersion();
                  setOpen(false);
                }}
                className="flex items-center gap-2 p-2 w-full rounded-md border-2 border-dashed border-primary bg-primary/5 hover:bg-primary/10 text-left transition-colors"
              >
                <div className="w-9 h-7 border-2 border-dashed border-primary rounded-sm flex items-center justify-center text-primary shrink-0">
                  <Plus className="size-4" />
                </div>
                <div className="flex-1">
                  <div className="text-xs font-semibold text-primary">Neue Version generieren</div>
                  <div className="text-[9px] text-primary/80">
                    Komplett neu ({newVersionPrice === 0 ? 'kostenlos' : `${newVersionPrice} Credits`})
                  </div>
                </div>
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};
